import React, {useEffect, useState} from "react";
import useTheme from "../../../components/web/layoutscommon/DarkModeStorage";
import {Btn, Notice} from "./StoreUI";
import {TbX, TbFileTypePdf, TbPresentation, TbEye, TbDownload, TbCheck} from "react-icons/tb";
import "../../../css/web/StoreDesign.css";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사 자료 받기 — 연동 가이드(PDF) · 상품 소개서(PPTX)
 * ════════════════════════════════════════════════════════════════════════════════
 *  [왜 이렇게 바뀌었나]
 *   예전에는 [HTML 다운로드] 와 [PDF로 저장] 두 버튼이 있었는데, 둘 다 '파일이
 *   저장되지 않는' 문제가 있었다.
 *     · HTML : 화면에서 문자열을 만들어 Blob 으로 내려받게 했는데, 만들자마자
 *              URL.revokeObjectURL() 로 주소를 회수해 브라우저/OS 조합에 따라
 *              저장이 취소되고 새 창만 열렸다.
 *     · PDF  : 애초에 다운로드가 아니라 window.print() — 즉 '인쇄 대화상자' 였다.
 *
 *   그래서 문서를 빌드 때 미리 만들어 public/docs 에 두고(scripts/buildStoreDocs.mjs),
 *   여기서는 '같은 출처의 정적 파일 주소' 를 a[download] 로 받기만 한다.
 *   Blob 을 만들지 않으므로 회수 경합이 없고, 어떤 브라우저에서도 다운로드 폴더에
 *   저장된다.
 *
 *  [보기 / 저장을 나눈 이유]
 *   바로 열어 훑고 싶은 사람과, 개발자에게 전달하려고 파일로 받고 싶은 사람이
 *   섞여 있다. 눌렀더니 생각과 다른 일이 벌어지지 않도록 선택지를 보여준다.
 *   PPTX 는 브라우저가 열 수 없으므로 [저장하기] 만 둔다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 받을 문서들 — 경로는 scripts/buildStoreDocs.mjs 의 산출물과 같아야 한다 */
export const STORE_DOCS = [
    {
        key: 'guide',
        icon: 'pdf',
        kind: 'PDF',
        name: '연동 가이드',
        desc: '서비스 설명부터 API 명세 · 버튼 규격까지 전부 담았습니다. 개발자분께 그대로 전달하셔도 됩니다.',
        url: '/docs/btorage-api-guide.pdf',
        saveAs: '비토리지 구매대행 API 연동 가이드.pdf',
        viewable: true,
    },
    {
        key: 'intro',
        icon: 'ppt',
        kind: 'PPTX',
        name: '상품 소개서',
        desc: '서비스 요약본입니다. 사내 공유나 검토용으로 쓰시기 좋습니다. (PowerPoint · 구글 슬라이드 · 한쇼에서 열립니다)',
        url: '/docs/btorage-api-intro.pptx',
        saveAs: '비토리지 구매대행 API 상품 소개서.pptx',
        viewable: false,
    },
];

/** 바이트를 사람이 읽는 크기로 */
const prettySize = (bytes) => {
    if (!bytes || bytes < 1024) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)}MB` : `${Math.round(bytes / 1024)}KB`;
};

/**
 * 같은 출처의 정적 파일을 다운로드 폴더에 저장한다.
 *
 * <p>Blob 을 만들지 않는 것이 핵심이다. a[download] 는 같은 출처 주소에 대해
 *  Content-Type 과 무관하게 '저장' 으로 동작하므로 새 창이나 미리보기로 새지 않는다.
 */
const saveFile = (url, filename) => {
    const a = document.createElement('a');
    if (!('download' in a)) {
        // download 속성을 모르는 아주 오래된 브라우저 — 최소한 열기라도 한다
        window.open(url, '_blank', 'noopener');
        return false;
    }
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    // 클릭이 처리될 틈을 준 뒤 치운다 (즉시 지우면 일부 브라우저가 취소한다)
    setTimeout(() => a.remove(), 0);
    return true;
};

const DocIcon = ({type}) => (
    <span
        aria-hidden="true"
        className="tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-[14px] tw-text-[21px]"
        style={type === 'pdf'
            ? {background: 'linear-gradient(135deg,#FFD79A,#FFBE48)', color: '#FFFFFF'}
            : {background: 'linear-gradient(135deg,#E3D9FF,#CBB6FF)', color: '#FFFFFF'}}>
        {type === 'pdf' ? <TbFileTypePdf/> : <TbPresentation/>}
    </span>
);

/**
 * @param open      표시여부
 * @param onClose   닫기 핸들러
 */
function StoreDocsModal({open, onClose}) {

    const [theme] = useTheme();
    const [sizes, setSizes] = useState({});
    const [issuedAt, setIssuedAt] = useState('');
    const [saved, setSaved] = useState('');

    /* 파일 크기·발행일은 빌드할 때 남겨둔 manifest 에서 읽는다 (화면에 하드코딩하지 않는다) */
    useEffect(() => {
        if (!open) return;
        let alive = true;
        fetch('/docs/manifest.json', {cache: 'no-cache'})
            .then(res => (res.ok ? res.json() : null))
            .then(data => {
                if (!alive || !data) return;
                setSizes({
                    guide: data?.files?.guidePdf?.bytes,
                    intro: data?.files?.introPptx?.bytes,
                });
                setIssuedAt(data?.issuedAt || '');
            })
            .catch(() => { /* 크기를 못 읽어도 받는 데는 지장이 없다 */ });
        return () => { alive = false; };
    }, [open]);

    /* ESC 로 닫기 */
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const onSave = (doc) => {
        saveFile(doc.url, doc.saveAs);
        setSaved(doc.key);
        setTimeout(() => setSaved(''), 2200);
    };

    return (
        <div className="st-root" data-theme={theme}
             style={{
                 position: 'fixed', inset: 0, zIndex: 9999, minHeight: 0,
                 background: 'rgba(27,23,48,.5)', backdropFilter: 'blur(8px)',
             }}
             role="dialog" aria-modal="true" aria-label="자료 받기"
             onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>

            <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-4">
                <div className="st-glass st-rise tw-flex tw-w-full tw-max-w-[600px] tw-flex-col tw-overflow-hidden"
                     style={{maxHeight: '88vh'}}>

                    {/*═══ 헤더 ═══*/}
                    <header className="tw-flex tw-flex-shrink-0 tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-px-6 tw-py-5"
                            style={{borderColor: 'var(--st-glass-line)'}}>
                        <div className="tw-min-w-0">
                            <p className="st-eyebrow tw-mb-1">Documents</p>
                            <h2 className="st-display tw-text-[18px] tw-text-[color:var(--st-text)]">자료 받기</h2>
                            <p className="tw-mt-1.5 tw-text-[12.5px] tw-leading-[1.7] tw-text-[color:var(--st-text-faint)] tw-break-keep">
                                바로 열어보시거나, 파일로 저장해 개발자분께 전달하실 수 있습니다.
                            </p>
                        </div>
                        <button type="button" onClick={onClose} aria-label="닫기"
                                className="st-btn st-btn-glass st-btn-sm st-btn-icon tw-flex-shrink-0 tw-text-[17px]">
                            <TbX/>
                        </button>
                    </header>

                    {/*═══ 문서 목록 ═══*/}
                    <div className="st-scroll tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-5">
                        <div className="tw-space-y-3">
                            {STORE_DOCS.map((doc) => (
                                <article key={doc.key}
                                         className="st-lift tw-rounded-[18px] tw-border tw-p-4 tw-transition-colors"
                                         style={{background: 'var(--st-field)', borderColor: 'var(--st-glass-line)'}}>
                                    <div className="tw-flex tw-items-start tw-gap-3.5">
                                        <DocIcon type={doc.icon}/>
                                        <div className="tw-min-w-0 tw-flex-1">
                                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
                                                <h3 className="tw-text-[14px] tw-font-bold tw-text-[color:var(--st-text)]">
                                                    {doc.name}
                                                </h3>
                                                <span className="st-mono tw-rounded-full tw-border tw-px-2 tw-py-[1px] tw-text-[10.5px] tw-font-bold"
                                                      style={{
                                                          background: 'rgba(255,190,72,.16)',
                                                          borderColor: 'rgba(255,190,72,.45)',
                                                          color: '#B8790F',
                                                      }}>
                                                    {doc.kind}
                                                </span>
                                                {prettySize(sizes[doc.key]) ? (
                                                    <span className="tw-text-[11.5px] tw-text-[color:var(--st-text-faint)]">
                                                        {prettySize(sizes[doc.key])}
                                                    </span>
                                                ) : null}
                                            </div>
                                            <p className="tw-mt-1.5 tw-text-[12.5px] tw-leading-[1.75] tw-text-[color:var(--st-text-sub)] tw-break-keep">
                                                {doc.desc}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="tw-mt-3.5 tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
                                        {doc.viewable ? (
                                            <Btn size="sm" tone="glass"
                                                 onClick={() => window.open(doc.url, '_blank', 'noopener')}>
                                                <TbEye/> 바로 보기
                                            </Btn>
                                        ) : null}
                                        <Btn size="sm" tone="dawn" onClick={() => onSave(doc)}>
                                            {saved === doc.key ? <><TbCheck/> 저장했습니다</> : <><TbDownload/> 저장하기</>}
                                        </Btn>
                                    </div>
                                </article>
                            ))}
                        </div>

                        <div className="tw-mt-4">
                            <Notice tone="info">
                                문서의 예제코드에는 <b className="st-mono">YOUR_API_KEY</b> 자리표시자가 들어 있습니다.
                                그 자리에 대시보드에서 발급받으신 키를 넣어 사용해주세요.
                                파일이 메일 등으로 전달되는 과정에서 키가 함께 새어나가지 않도록, 실제 키는 화면에서만 보여드립니다.
                            </Notice>
                        </div>
                    </div>

                    {/*═══ 푸터 ═══*/}
                    <footer className="tw-flex tw-flex-shrink-0 tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-px-6 tw-py-4"
                            style={{borderColor: 'var(--st-glass-line)'}}>
                        <p className="tw-text-[11.5px] tw-text-[color:var(--st-text-faint)]">
                            {issuedAt ? `${issuedAt} 기준` : ' '}
                        </p>
                        <Btn size="md" tone="glass" onClick={onClose} className="tw-min-w-[96px]">닫기</Btn>
                    </footer>
                </div>
            </div>
        </div>
    );
}

export default StoreDocsModal;
